import axios from "axios";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";

/**
 * The sender's claimed brand subdomain (kitecorp/transferify-api-service#350).
 *
 * Separate from {@link AccountBrandApi} because the surfaces have different gates: branding is refused to
 * a non-entitled sender on every verb, whereas reading and RELEASING a claim stay open on every tier so a
 * downgraded sender can still see and give back the name they hold.
 */

/** GET/POST `/account/subdomain` payload. */
export type SubdomainClaim = {
	/** The claimed label, without any brand suffix, e.g. `acme`. */
	label: string;
	/**
	 * Every brand origin this claim serves recipient links on, e.g. `https://acme.transferify.cloud` and
	 * `https://acme.transferify.ro`. Server-derived: the client must never compose a brand suffix, or a new
	 * brand (and the dev hosts) would show the wrong address here.
	 */
	hosts: string[];
	/** ISO-8601 instant the label was claimed. */
	claimedAt: string;
};

/** POST `/account/subdomain` body. */
export interface SubdomainClaimRequest {
	label: string;
}

export type _SubdomainClaim = AssertNoSpecDrift<SpecDrift<SubdomainClaim, Schemas["SubdomainClaimView"]>>;
export type _SubdomainClaimRequest =
	AssertNoSpecDrift<RequestSpecDrift<SubdomainClaimRequest, Schemas["SubdomainClaimRequest"]>>;

/**
 * Whether a response body is actually a claim, rather than merely a 200.
 *
 * The cast this replaces turned ANY 200 body into a `SubdomainClaim`, so a payload without `hosts` reached
 * the editor as a claim and died in `claim.hosts.map(...)` — a render throw, which React Router's error
 * boundary turns into a blank route, taking the whole personalization sheet down with it (ui#534). A
 * missing field must degrade to "nothing claimed", never to a crash: the editor then shows the same state
 * a 204 produces, which is the truthful reading of a body that names no label.
 *
 * `label` and `hosts` are checked because they are the two the editor dereferences. `claimedAt` is carried
 * but never read, so demanding it would reject a usable claim over a field nothing needs.
 */
function asClaim(data: unknown): SubdomainClaim | null {
	if (typeof data !== "object" || data === null) {
		return null;
	}
	const {label, hosts} = data as Partial<SubdomainClaim>;
	return typeof label === "string" && label.length > 0 && Array.isArray(hosts)
		? (data as SubdomainClaim)
		: null;
}

export class AccountSubdomainApi {
	/**
	 * The current claim, or `null` when nothing is claimed — the api answers 204 with no body for that,
	 * which axios surfaces as an empty string rather than an object.
	 */
	static async getClaim(): Promise<SubdomainClaim | null> {
		const {status, data} = await axios.get<SubdomainClaim | "">("/account/subdomain");
		return status === 204 ? null : asClaim(data);
	}

	static async claim(label: string): Promise<SubdomainClaim | null> {
		const body: SubdomainClaimRequest = {label};
		const {data} = await axios.post<SubdomainClaim>("/account/subdomain", body);
		return asClaim(data);
	}

	static async release(): Promise<void> {
		await axios.delete("/account/subdomain");
	}
}
